/**
 * My sessions — the student's and the mentor's own bookings.
 *
 * *** ONE SCREEN, BOTH ROLES ***
 * `/bookings` returns the caller's own rows whichever side they are on: a
 * student sees what they booked, a mentor sees what was booked with them.
 * The server decides; this screen only changes which NAME it shows, because
 * "with Priya" is the useful line for a student and "with Rahul" for a
 * mentor.
 *
 * *** NOT THE ADMIN BOOKINGS TABLE ***
 * That is `/admin/bookings`, a support tool listing every session in the
 * tenant with no way to join one. This is the participant's view.
 *
 * *** THE JOIN BUTTON IS THE SERVER'S DECISION ***
 * `meeting_ready` and `can_join_from` arrive computed. A screen that worked
 * out its own window would offer Join on a session the server then refuses,
 * which is worse than a button that is honestly disabled.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader, PageStack, Card } from '@/components/ui/Page'
import { Button } from '@/components/ui/Button'
import { Pill, BookingStatusPill } from '@/components/ui/Pill'
import { useToast } from '@/components/ui/Toast'
import { ApiError, resolveMeetingUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useMyBookings } from '@/lib/queries'
import {
  differsInTimezone,
  formatDateTime,
  formatDuration,
  formatInTimezone,
  formatMoney,
} from '@/lib/format'
import type { MyBooking } from '@/types/api'
import { CancelSessionDialog } from './CancelSessionDialog'
import { RateSessionDialog } from './RateSessionDialog'
import styles from './SessionsPage.module.css'

type Tab = 'live' | 'upcoming' | 'past'

/**
 * Is this session happening right now?
 *
 * *** WHY THIS IS NOT A SERVER FILTER ***
 * `/bookings?upcoming=` splits on `slot_start_utc` alone, so the moment a
 * 3:00 PM session begins it moves into the PAST list — and that is exactly
 * when it matters most. Reported 2026-10-31: a live session sitting under
 * "Past" with a Join button on it.
 *
 * The window is wider than the slot at the front: `can_join_from` is the
 * start minus the tenant's join window (5 minutes by default), and someone
 * who may already enter the room is, for this screen's purposes, live. So a
 * live row can come from EITHER server list — the upcoming one during those
 * first few minutes, the past one once the clock passes the start.
 *
 * Deliberately NOT `canJoinNow`. That answers "may the button be pressed",
 * which also requires `meeting_ready`. A CONFIRMED session whose Meet link
 * failed to provision is still happening; hiding it from Live would lose
 * the one row the person is looking for, and its disabled button carries
 * the explanation.
 */
function isLive(booking: MyBooking, now: number): boolean {
  if (booking.status !== 'CONFIRMED') return false

  const ends = new Date(booking.slot_end_utc).getTime()
  if (Number.isNaN(ends) || now > ends) return false

  /*
    Falls back to the slot start when the server sent no window — never to
    "live", or every future booking would land in this tab.
  */
  const opens = booking.can_join_from
    ? new Date(booking.can_join_from).getTime()
    : new Date(booking.slot_start_utc).getTime()
  if (Number.isNaN(opens)) return false

  return now >= opens
}

/**
 * May this session be joined right now?
 *
 * *** `meeting_ready` IS NOT ENOUGH ***
 * It means "a conference link exists for this booking", and it stays true
 * forever — so a COMPLETED session from three weeks ago still carried it
 * and the screen offered Join on it (reported 2026-10-31).
 *
 * The server's join route enforces the real window and would have refused,
 * so this was never a way into an old meeting. But a button that fails when
 * pressed is its own bug: it tells someone their session is still open.
 *
 * Three conditions, all required:
 *   1. a link exists at all
 *   2. the booking is still live — not cancelled, not finished
 *   3. the join window has opened, and the session has not yet ended
 */
function canJoinNow(booking: MyBooking, now: number): boolean {
  if (!booking.meeting_ready) return false

  // COMPLETED and NO_SHOW are over. PENDING_PAYMENT has not been paid for.
  if (booking.status !== 'CONFIRMED') return false

  const opens = booking.can_join_from
    ? new Date(booking.can_join_from).getTime()
    : NaN
  if (!Number.isNaN(opens) && now < opens) return false

  /*
    *** AND IT CLOSES AT THE END ***
    Without this, a CONFIRMED session that nobody marked complete would
    offer Join indefinitely. The end time is the booking's own, so a
    session running late is still joinable until its slot is genuinely
    over.
  */
  const ends = new Date(booking.slot_end_utc).getTime()
  if (!Number.isNaN(ends) && now > ends) return false

  return true
}

/**
 * Why the Join button is disabled, in words — or null when it is enabled
 * and when there is nothing useful to say.
 */
function joinHint(booking: MyBooking, tab: Tab, now: number): string | null {
  if (canJoinNow(booking, now)) return null
  if (booking.status === 'CANCELLED') return null

  /*
    Over. Worth saying on the UPCOMING tab, where a session that just
    finished is still listed and its dead button needs explaining. On the
    Past tab every row has ended, so the line is dropped as noise.
  */
  const ends = new Date(booking.slot_end_utc).getTime()
  if (!Number.isNaN(ends) && now > ends) {
    return tab === 'past' ? null : 'This session has ended'
  }

  if (booking.status === 'PENDING_PAYMENT') return 'Not paid for yet'
  if (!booking.meeting_ready) return 'The meeting is not ready yet'

  if (booking.can_join_from) {
    const opens = new Date(booking.can_join_from)
    if (!Number.isNaN(opens.getTime()) && opens.getTime() > now) {
      return `You can join from ${formatDateTime(booking.can_join_from)}`
    }
  }

  return null
}

/**
 * How long is left, as a sentence. Live rows only.
 *
 * Before the start it counts down to it; after, it counts down to the end.
 * Both are the question the person actually has open on this tab.
 */
function liveNote(booking: MyBooking, now: number): string {
  const starts = new Date(booking.slot_start_utc).getTime()
  const ends = new Date(booking.slot_end_utc).getTime()

  if (!Number.isNaN(starts) && now < starts) {
    const mins = Math.max(1, Math.round((starts - now) / 60_000))
    return `Starts in ${mins} min`
  }
  if (!Number.isNaN(ends)) {
    const mins = Math.max(1, Math.round((ends - now) / 60_000))
    return `${mins} min remaining`
  }
  return 'In progress'
}

/**
 * A clock that ticks on its own.
 *
 * *** THE LIST POLL IS NOT ENOUGH ***
 * `useMyBookings` refetches every 60s, and without this the Live partition
 * would only be recomputed when it did — so a finished session could sit in
 * the Live tab for up to a minute, and a Join button stay lit after its
 * window closed. This re-renders on a 20s tick using data already in hand,
 * with no extra requests.
 */
function useNow(intervalMs = 20_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(id)
  }, [intervalMs])
  return now
}

export function SessionsPage() {
  const { user } = useAuth()
  const isMentor = user?.role === 'MENTOR'
  const toast = useToast()
  const navigate = useNavigate()
  const now = useNow()

  const [tab, setTab] = useState<Tab>('upcoming')
  const [cancelling, setCancelling] = useState<MyBooking | null>(null)
  const [rating, setRating] = useState<MyBooking | null>(null)
  const [joining, setJoining] = useState<string | null>(null)

  /*
    *** BOTH SERVER LISTS, ALWAYS ***
    A live session can be in either one — see `isLive`. Fetching only the
    list for the open tab would make Live depend on which tab was clicked
    first, and would leave the Live badge wrong until the person switched.

    Two queries, both already cached and polling; the cost is one extra
    request per minute.
  */
  const future = useMyBookings(true)
  const history = useMyBookings(false)

  const futureRows = useMemo(
    () => future.data?.bookings ?? [],
    [future.data],
  )
  const pastRows = useMemo(() => history.data?.bookings ?? [], [history.data])

  const live = useMemo(
    () =>
      [...futureRows, ...pastRows]
        .filter((b) => isLive(b, now))
        // Earliest first: the one that ends soonest is the one being missed.
        .sort(
          (a, b) =>
            new Date(a.slot_start_utc).getTime() -
            new Date(b.slot_start_utc).getTime(),
        ),
    [futureRows, pastRows, now],
  )

  /*
    A live row is shown on its own tab and taken out of the other two, so
    the same session never appears twice. The server's own ordering is kept
    — ascending for upcoming, descending for past.
  */
  const liveIds = useMemo(
    () => new Set(live.map((b) => b.booking_id)),
    [live],
  )
  const upcoming = useMemo(
    () => futureRows.filter((b) => !liveIds.has(b.booking_id)),
    [futureRows, liveIds],
  )
  const past = useMemo(
    () => pastRows.filter((b) => !liveIds.has(b.booking_id)),
    [pastRows, liveIds],
  )

  const bookings = tab === 'live' ? live : tab === 'upcoming' ? upcoming : past

  /*
    *** HAS THIS PERSON EVER BOOKED? ***
    Only the PAST list can answer it, and only once it has answered: until
    then `pastRows` is empty because nothing has loaded, which is not the
    same as empty because nothing exists. Defaulting to `true` while the
    answer is unknown means the wording falls back to the one that is right
    either way — "Book a session" is never wrong, "your first" is wrong for
    anyone with history.
  */
  const everBooked = !history.isSuccess || pastRows.length > 0

  const query = tab === 'past' ? history : future
  // Live reads both lists, so it is only settled once both are.
  const settled =
    tab === 'live'
      ? future.isSuccess &&
        history.isSuccess &&
        !future.isFetching &&
        !history.isFetching
      : query.isSuccess && !query.isFetching

  /*
    *** THE SAME RULE AS THE LIBRARY LOBBY ***
    `isLoading` is false on a revisit, so checking only that would announce
    "no sessions" from a cached list. An empty state is a claim; it waits
    for a settled answer.
  */
  const reallyEmpty = settled && bookings.length === 0
  const error = tab === 'past' ? history.error : future.error
  const loading =
    tab === 'live'
      ? (future.isLoading || history.isLoading) && live.length === 0
      : query.isLoading || (query.isFetching && bookings.length === 0)

  const join = async (booking: MyBooking) => {
    setJoining(booking.booking_id)
    try {
      const url = await resolveMeetingUrl(booking.booking_id)
      /*
        A new tab, not this one. Leaving the portal would lose an unsaved
        state and make "back" the only way to their session list.
        `noopener` because the meeting is a third-party page.
      */
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (caught) {
      /*
        The server names which check failed — not ready, outside the window,
        not your booking. Replacing that with "could not join" throws away
        the only sentence that says what to do.
      */
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not open the meeting.',
      )
    } finally {
      setJoining(null)
    }
  }

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'live', label: 'Live', count: live.length },
    { id: 'upcoming', label: 'Upcoming' },
    { id: 'past', label: 'Past' },
  ]

  return (
    <PageStack>
      <PageHeader
        title="My sessions"
        description={
          isMentor
            ? 'Sessions booked with you.'
            : 'Your one-to-one sessions with a mentor.'
        }
        /*
          *** STUDENTS ONLY ***
          A mentor does not book sessions, they receive them. The booking
          screen would show them an empty mentor list, because that list is
          scoped to the caller's own student category.
        */
        actions={
          isMentor ? undefined : (
            <Button variant="primary" onClick={() => navigate('/sessions/book')}>
              Book a session
            </Button>
          )
        }
      />

      {/*
        Live leads, then Upcoming. Live is usually empty, which is why it
        carries a count — an empty tab with no number looks broken, and a
        tab showing "1" is the fastest way to a meeting in progress.
      */}
      <div className={styles.tabs} role="tablist" aria-label="Which sessions">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`${styles.tab} ${tab === t.id ? styles.tabActive : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {/* Only when there IS one. A grey "0" beside every tab is noise. */}
            {t.count ? <span className={styles.tabCount}>{t.count}</span> : null}
          </button>
        ))}
      </div>

      {loading && <p className={styles.muted}>Loading your sessions…</p>}

      {error && (
        <p className={styles.error}>
          {error instanceof ApiError
            ? error.message
            : 'Could not load your sessions.'}
        </p>
      )}

      {reallyEmpty && !error && (
        <Card>
          <p className={styles.muted}>
            {tab === 'live'
              ? 'Nothing is running right now. A session appears here when it is about to start.'
              : tab === 'upcoming'
                ? isMentor
                  ? 'No upcoming sessions. Students will appear here once they book with you.'
                  : everBooked
                    ? 'Nothing booked at the moment.'
                    : 'No upcoming sessions yet.'
                : 'No past sessions.'}
          </p>
          {/*
            An empty state that only states a fact leaves the person nowhere
            to go. A student can act on this one.

            *** NOT "YOUR FIRST SESSION" ***
            That claimed something about their history this screen had not
            checked — it looked only at the UPCOMING list, so a student with
            twenty past sessions was told to book their first (reported
            2026-10-31). The past list is already loaded for the Live tab,
            so the question is answerable rather than guessable.
          */}
          {tab === 'upcoming' && !isMentor && (
            <div className={styles.emptyAction}>
              <Button
                variant="primary"
                onClick={() => navigate('/sessions/book')}
              >
                {everBooked ? 'Book a session' : 'Book your first session'}
              </Button>
            </div>
          )}
        </Card>
      )}

      <div className={styles.list}>
        {bookings.map((booking) => {
          /*
            *** WHOSE NAME TO SHOW ***
            A student's list is useless without the mentor's name and a
            mentor's without the student's. Each side sees the OTHER party.
          */
          const other = isMentor ? booking.student_name : booking.mentor_name
          const hint = joinHint(booking, tab, now)
          const cancelled = booking.status === 'CANCELLED'
          const joinable = canJoinNow(booking, now)

          /*
            Rating opens when the session STARTS, not when it is marked
            complete — the server's rule, and nobody marks them complete
            promptly. It closes once a rating exists, because a second one
            is refused.
          */
          const rated = !isMentor && booking.my_rating !== null
          const rateable =
            !isMentor &&
            !cancelled &&
            booking.my_rating === null &&
            new Date(booking.slot_start_utc).getTime() < now
          const running = tab === 'live'

          return (
            <article
              key={booking.booking_id}
              className={`${styles.card} ${running ? styles.cardLive : ''}`}
            >
              <div className={styles.cardMain}>
                <header className={styles.cardHead}>
                  <div>
                    <h3 className={styles.when}>
                      {formatDateTime(booking.slot_start_utc)}
                    </h3>
                    {/*
                      *** THE MENTOR'S ZONE, WHEN IT DIFFERS ***
                      A session is scheduled in the mentor's working day. A
                      student in another zone needs both times or they will
                      read the wrong one — the admin table already does this.
                    */}
                    {differsInTimezone(
                      booking.slot_start_utc,
                      booking.mentor_timezone,
                    ) && (
                      <p className={styles.altTime}>
                        {formatInTimezone(
                          booking.slot_start_utc,
                          booking.mentor_timezone,
                        )}{' '}
                        for the mentor
                      </p>
                    )}
                  </div>
                  {/* On the Live tab the status pill would read "Confirmed"
                      on every row, which says nothing. The time left does. */}
                  {running ? (
                    <span className={styles.livePill}>
                      <span className={styles.liveDot} aria-hidden="true" />
                      {liveNote(booking, now)}
                    </span>
                  ) : (
                    <BookingStatusPill status={booking.status} />
                  )}
                </header>

                <dl className={styles.meta}>
                  <div>
                    <dt>{isMentor ? 'Student' : 'Mentor'}</dt>
                    <dd>{other ?? '—'}</dd>
                  </div>
                  <div>
                    <dt>Length</dt>
                    <dd>
                      {booking.session_type_name
                        ? `${booking.session_type_name} · ${formatDuration(booking.duration_minutes)}`
                        : formatDuration(booking.duration_minutes)}
                    </dd>
                  </div>
                  <div>
                    <dt>Price</dt>
                    <dd>
                      {/*
                        *** NOT A BILL ON A SUBSCRIPTION CLIENT ***
                        A plan booking carries a price snapshot for
                        reporting, but nobody was charged it. Shown as money
                        it reads as an amount owed — a mentor once saw
                        "INR 1261.34" on a session the student saw as one
                        session from their plan.
                      */}
                      {booking.subscription_billing ? (
                        <Pill tone="info" subtle>
                          From a plan
                        </Pill>
                      ) : (
                        formatMoney(booking.total_amount, booking.currency)
                      )}
                    </dd>
                  </div>
                </dl>

                {cancelled && booking.cancellation_reason && (
                  <p className={styles.cancelled}>
                    Cancelled
                    {booking.cancelled_by_role
                      ? ` by the ${booking.cancelled_by_role.toLowerCase()}`
                      : ''}
                    : {booking.cancellation_reason}
                  </p>
                )}
              </div>

              {/* Nothing to act on — a past, completed session — so no
                  footer at all rather than an empty bordered strip. */}
              {(joinable || tab !== 'past' || hint || rateable || rated) && (
                <footer className={styles.actions}>
                  {/*
                    *** HIDDEN ON A FINISHED SESSION, NOT JUST DISABLED ***
                    A greyed-out Join on a three-week-old completed session
                    is still clutter that suggests it might work. A past
                    list wants the record, not a control.
                  */}
                  {(joinable || tab !== 'past') && (
                    <Button
                      variant="primary"
                      onClick={() => join(booking)}
                      disabled={!joinable || joining === booking.booking_id}
                    >
                      {joining === booking.booking_id
                        ? 'Opening…'
                        : 'Join the meeting'}
                    </Button>
                  )}

                  {/* Only sessions that have not finished can be cancelled,
                      and the dialog asks the server what that would cost
                      before committing. A session already in progress is
                      not cancellable — leaving it is not a cancellation. */}
                  {tab === 'upcoming' && !cancelled && (
                    <Button
                      variant="ghost"
                      onClick={() => setCancelling(booking)}
                    >
                      Cancel
                    </Button>
                  )}

                  {/*
                    *** RATING IS A STUDENT'S ACT, ON A SESSION THAT RAN ***
                    The server allows it from the moment the session starts
                    and refuses a second one, so the button appears once
                    `slot_start_utc` has passed and disappears once
                    `my_rating` is set. A mentor never sees it — they are
                    the one being rated.
                  */}
                  {rateable && (
                    <Button
                      variant="secondary"
                      onClick={() => setRating(booking)}
                    >
                      Rate this session
                    </Button>
                  )}

                  {/* Already rated: the score, not a dead control. */}
                  {rated && (
                    <span className={styles.rated}>
                      You rated this {booking.my_rating}
                      <span aria-hidden="true"> ★</span>
                    </span>
                  )}

                  {/* The reason sits beside the disabled button — a greyed
                      control with no explanation is the failure to avoid. */}
                  {hint && <span className={styles.hint}>{hint}</span>}
                </footer>
              )}
            </article>
          )
        })}
      </div>

      {cancelling && (
        <CancelSessionDialog
          booking={cancelling}
          onClose={() => setCancelling(null)}
        />
      )}

      {rating && (
        <RateSessionDialog
          booking={rating}
          onClose={() => setRating(null)}
        />
      )}
    </PageStack>
  )
}
